/**
 * Reason-based highlight registry: an element carries the highlight class
 * while at least one reason points at it. Callers move their own reason and
 * never need to know why anyone else highlighted an element - the guards that
 * used to cross-reference hover/selection/card state disappear.
 */
export type TargetHighlight = {
  /** Point `reason` at `el` (or nothing); the previous holder is released. */
  moveReason: (reason: string, el: Element | null) => void;
  /** Replace every element held by `reason`. */
  setReason: (reason: string, elements: readonly Element[]) => void;
  clear: () => void;
};

export function createTargetHighlight(className: string): TargetHighlight {
  const reasonElements = new Map<string, Set<Element>>();
  const counts = new Map<Element, number>();

  const retain = (el: Element): void => {
    counts.set(el, (counts.get(el) ?? 0) + 1);
    el.classList.add(className);
  };

  const release = (el: Element): void => {
    const next = (counts.get(el) ?? 0) - 1;
    if (next <= 0) {
      counts.delete(el);
      el.classList.remove(className);
    } else {
      counts.set(el, next);
    }
  };

  return {
    moveReason: (reason, el) => {
      const previous = reasonElements.get(reason);
      if (previous?.size === 1 && previous.has(el as Element)) return;
      for (const held of previous ?? []) release(held);
      if (el) retain(el);
      if (el) reasonElements.set(reason, new Set([el]));
      else reasonElements.delete(reason);
    },
    setReason: (reason, elements) => {
      const previous = reasonElements.get(reason) ?? new Set<Element>();
      const next = new Set(elements);
      for (const held of previous) if (!next.has(held)) release(held);
      for (const held of next) if (!previous.has(held)) retain(held);
      if (next.size) reasonElements.set(reason, next);
      else reasonElements.delete(reason);
    },
    clear: () => {
      for (const elements of reasonElements.values()) for (const el of elements) release(el);
      reasonElements.clear();
    },
  };
}
